import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl'
import { PageTitle } from '../../../_metronic/layout/core';
import { getStatistics, syncStatistics } from './api';
import { StatisticsModel } from './StatisticsModel';
import { Border } from 'victory';

const cardStyle = {
    background: '#f5f8fa',
    borderRadius: 12,
    padding: 24,
    boxShadow: '0 1px 4px #0001',
    minWidth: 260,
    flex: 1,
    margin: 8,
    border: '1px solid #ccc',
};

const DashboardPage: React.FC = () => {
    const intl = useIntl();
    const [statistics, setStatistics] = useState<StatisticsModel | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const fetchAndSyncStatistics = async () => {
            try {
                await syncStatistics();
                const response = await getStatistics();
                setStatistics(response.data);
            } catch (error) {
                setStatistics(null);
            } finally {
                setLoading(false);
            }
        };
        fetchAndSyncStatistics();
    }, []);

    return (
        <>
            <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'MENU.DASHBOARD', defaultMessage: 'Dashboard'})}</PageTitle>
            <div className='card card-custom'>
                <div className='card-body'>
                    {/* Top summary cards */}
                    <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
                        <div style={{ ...cardStyle }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 22 }}>
                                <span style={{ color: '#43b649', fontSize: 28 }}>🛡️</span> System Health
                            </div>
                            <div
                                style={{
                                    color:
                                        !loading && statistics?.offlineChargers && parseInt(statistics.offlineChargers) > 0
                                            ? '#F1416C'
                                            : '#43b649',
                                    fontSize: '3em',
                                    margin: '8px 0',
                                }}
                            >
                                {loading ? '...' : statistics?.offlineChargers ?? '0'}
                            </div>
                            <div style={{ color: '#444', fontSize: 16 }}>
                                Offline out of <b>{loading ? '...' : statistics?.allChargers ?? '0'}</b> total
                            </div>
                        </div>
                        <div style={{ ...cardStyle }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 22 }}>
                                <span style={{ color: '#2196f3', fontSize: 28 }}>🔄</span> Active Sessions
                            </div>
                            <div style={{ color: '#2196f3', fontSize: '3em', margin: '8px 0' }}>
                                {loading ? '...' : statistics?.activeSessions ?? '0'}
                            </div>
                            <div style={{ color: '#444', fontSize: 16 }}>Currently Charging</div>
                        </div>
                        <div style={{ ...cardStyle, opacity: 0.3 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 22 }}>
                                <span style={{ color: '#ff9800', fontSize: 28 }}>⚡</span> Active kWh
                            </div>
                            <div style={{ color: '#ff9800', fontSize: '3em', margin: '8px 0' }}>
                                {loading ? '...' : statistics?.activeKwH ?? '0'} <span style={{ fontSize: '0.5em' }}>kWh</span>
                            </div>
                            <div style={{ color: '#444', fontSize: 16 }}>From Active Sessions</div>
                        </div>
                        <div style={{ ...cardStyle, opacity: 0.3 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 22 }}>
                                <span style={{ color: '#43b649', fontSize: 28 }}>🚌</span> Completed Sessions
                            </div>
                            <div style={{ color: '#43b649', fontSize: '3em', margin: '8px 0' }}>
                                {loading ? '...' : statistics?.completedSessions ?? '0'}
                            </div>
                            <div style={{ color: '#444', fontSize: 16 }}>From Active Sessions</div>
                        </div>
                    </div>

                    {/* Filter Bar */}
                    <div style={{ opacity: 0.3, background: '#fff', borderRadius: 10, padding: 20, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 1px 4px #0001' }}>
                        <span style={{ fontWeight: 500 }}>Time Period</span>
                        <select style={{ padding: 8, borderRadius: 6, border: '1px solid #bbb', minWidth: 120 }}>
                            <option>Last 7 days</option>
                        </select>
                        <span style={{ fontWeight: 500 }}>Charger Type</span>
                        <select style={{ padding: 8, borderRadius: 6, border: '1px solid #bbb', minWidth: 120 }}>
                            <option>Private</option>
                        </select>
                        <span style={{ fontWeight: 500 }}>Charger Status</span>
                        <select style={{ padding: 8, borderRadius: 6, border: '1px solid #bbb', minWidth: 120 }}>
                            <option>All</option>
                        </select>
                        <span style={{ fontWeight: 500 }}>From Date:</span>
                        <input type="text" placeholder="dd/mm/yyyy" style={{ padding: 8, borderRadius: 6, border: '1px solid #bbb', minWidth: 120 }} />
                        <span style={{ fontWeight: 500 }}>To Date:</span>
                        <input type="text" placeholder="dd/mm/yyyy" style={{ padding: 8, borderRadius: 6, border: '1px solid #bbb', minWidth: 120 }} />
                        <button className='btn btn-sm btn-primary'>Apply Custom Range</button>
                    </div>

                    {/* Bottom summary cards */}
                    <div style={{ opacity: 0.3, display: 'flex', gap: 16, marginBottom: 24 }}>
                        <div style={{ ...cardStyle }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 22 }}>
                                <span style={{ color: '#43b649', fontSize: 28 }}>$</span> Revenue Share
                            </div>
                            <div style={{ color: '#111', fontSize: '3em', margin: '8px 0' }}>
                                {loading ? '...' : statistics?.revenueShare ?? '$0'}
                            </div>
                            <div style={{ color: '#43b649', fontSize: 16, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span style={{ fontSize: 20 }}>↗</span> 4.7% increase
                            </div>
                        </div>
                        <div style={{ ...cardStyle }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 22 }}>
                                <span style={{ color: '#ffb300', fontSize: 28 }}>⚡</span> Total Energy
                            </div>
                            <div style={{ color: '#111', fontSize: '3em', margin: '8px 0' }}>
                                {loading ? '...' : statistics?.totalEnergy ?? '0'} <span style={{ fontSize: '0.5em' }}>kWh</span>
                            </div>
                            <div style={{ color: '#444', fontSize: 16 }}>From Completed Sessions only</div>
                        </div>
                        <div style={{ ...cardStyle }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 22 }}>
                                <span style={{ color: '#673ab7', fontSize: 28 }}>📊</span> Utilisation Rate
                            </div>
                            <div style={{ color: '#111', fontSize: '3em', margin: '8px 0' }}>
                                {loading ? '...' : statistics?.utilisationRate ?? '0%'}
                            </div>
                            <div style={{ color: '#444', fontSize: 16 }}>Current Network Utilisation</div>
                        </div>
                        <div style={{ ...cardStyle }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 22 }}>
                                <span style={{ color: '#388e3c', fontSize: 28 }}>🌱</span> CO₂ Emissions
                            </div>
                            <div style={{ color: '#111', fontSize: '3em', margin: '8px 0' }}>
                                {loading ? '...' : statistics?.cO2Emissions ?? '0g'} <span style={{ fontSize: '0.5em' }}>per km</span>
                            </div>
                            <div style={{ color: '#444', fontSize: 16 }}>CO₂ emissions per km</div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default DashboardPage
